import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import clientsService, { Client, ClientCreateInput, ClientUpdateInput } from '../../services/clientsService';

interface ClientsState {
  clients: Client[];
  selectedClient: Client | null;
  loading: boolean;
  error: string | null;
}

const initialState: ClientsState = {
  clients: [],
  selectedClient: null,
  loading: false,
  error: null,
};

export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (userId: string) => {
    const response = await clientsService.getClients(userId);
    console.log('[response]', response);
    return response;
  }
);

export const fetchClientById = createAsyncThunk(
  'clients/fetchClientById',
  async (params: { id: number; userId: string }) => {
    const response = await clientsService.getClientById(params.id, params.userId);
    return response;
  }
);

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (clientData: ClientCreateInput, { rejectWithValue }) => {
    try {
      const response = await clientsService.createClient(clientData);
      return response;
    } catch (error: any) {
      // Carry the backend's `{ message, errors }` body through as the
      // rejected payload (instead of RTK's default serialized-error, which
      // drops it) so callers' `.unwrap()` catch can read field-level
      // validation errors, e.g. `errors.email` on a 422 duplicate-email.
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async (clientData: ClientUpdateInput, { rejectWithValue }) => {
    try {
      const response = await clientsService.updateClient(clientData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (params: { id: number; userId: string }, { rejectWithValue }) => {
    try {
      await clientsService.deleteClient(params.id, params.userId);
      return params.id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setSelectedClient: (state, action) => {
      state.selectedClient = action.payload;
    },
    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        const clients = action.payload.data;
        const formattedClients = clients.map((client: Client) => {
          client.created_at = new Date(client?.created_at).toLocaleDateString()
          return client
        })
        state.clients = formattedClients;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch clients';
      })
      // Create client
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients.push(action.payload.data);
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to create client';
      })
      // Update client
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.clients.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
        if (state.selectedClient?.id === action.payload.id) {
          state.selectedClient = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to update client';
      })
      // Delete client
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = state.clients.filter((c) => c.id !== action.payload);
        if (state.selectedClient?.id === action.payload) {
          state.selectedClient = null;
        }
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to delete client';
      });
  },
});

export const { setSelectedClient, clearSelectedClient } = clientsSlice.actions;

export default clientsSlice.reducer; 